import type { AccountKeys } from './accountKeys.ts';
import { fromBase64Url, randomBytes, toBase64Url, utf8 } from './encoding.ts';
import { VaultError } from './vault.ts';

/**
 * A letter: a small message from one account to another, which only the recipient can
 * read and only the sender can have written (docs/SHARING-PLAN.md section 3.3). An
 * invitation is one: the realm's name and the role, sealed to the member by the owner.
 * The server carries letters but can neither read nor forge them.
 *
 * - **Encrypted** under AES-256-GCM with a key from HKDF over the ECDH secret of the
 *   sender's and recipient's exchange keys, with a fresh salt each time.
 * - **Signed** by the sender's signing key, since either side of an ECDH secret could
 *   have encrypted it.
 * - **Bound** to both account ids, in the key, the authenticated data and the signature,
 *   so a letter can't be passed off as between other accounts.
 *
 * Format: base64url of version(1) | salt(16) | iv(12) | signature(64) | ciphertext.
 */

const VERSION = 1;
const CONTEXT = 'realmery-letter-1';
const EXCHANGE = { name: 'ECDH', namedCurve: 'P-256' } as const;
const SIGNING = { name: 'ECDSA', namedCurve: 'P-256' } as const;
const SIGN = { name: 'ECDSA', hash: 'SHA-256' } as const;
const HEAD = 1 + 16 + 12;
const SIGNATURE = 64;

/** The other side of a letter: an account id and its public keys, as the server lists them. */
export interface Correspondent {
  readonly userId: string;
  readonly exchangePublic: string;
  readonly signingPublic: string;
}

function context(from: string, to: string): Uint8Array<ArrayBuffer> {
  return utf8(`${CONTEXT}\0${from}\0${to}`);
}

async function letterKey(mine: CryptoKey, theirs: string, salt: Uint8Array<ArrayBuffer>, from: string, to: string): Promise<CryptoKey> {
  const publicKey = await crypto.subtle.importKey('raw', fromBase64Url(theirs), EXCHANGE, false, []);
  const secret = await crypto.subtle.deriveBits(
    // The Workers types spell the field `$public`; browsers and Node read `public`.
    { name: 'ECDH', public: publicKey } as unknown as Parameters<typeof crypto.subtle.deriveBits>[0],
    mine,
    256,
  );
  const hkdf = await crypto.subtle.importKey('raw', secret as ArrayBuffer, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: context(from, to) },
    hkdf,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

function signed(from: string, to: string, head: Uint8Array, ciphertext: Uint8Array): Uint8Array<ArrayBuffer> {
  const ctx = context(from, to);
  const out = new Uint8Array(ctx.length + 1 + head.length + ciphertext.length);
  out.set(ctx);
  out.set(head, ctx.length + 1);
  out.set(ciphertext, ctx.length + 1 + head.length);
  return out;
}

/** Seals `payload` (any JSON) from the account `from` to `to`. */
export async function sealLetter(keys: AccountKeys, from: string, to: Pick<Correspondent, 'userId' | 'exchangePublic'>, payload: unknown): Promise<string> {
  const salt = randomBytes(16);
  const iv = randomBytes(12);
  const key = await letterKey(keys.exchange.privateKey, to.exchangePublic, salt, from, to.userId);
  const ciphertext = new Uint8Array(
    (await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: context(from, to.userId) }, key, utf8(JSON.stringify(payload)))) as ArrayBuffer,
  );
  const head = new Uint8Array(HEAD);
  head[0] = VERSION;
  head.set(salt, 1);
  head.set(iv, 17);
  const signature = new Uint8Array((await crypto.subtle.sign(SIGN, keys.signing.privateKey, signed(from, to.userId, head, ciphertext))) as ArrayBuffer);
  const out = new Uint8Array(HEAD + SIGNATURE + ciphertext.length);
  out.set(head);
  out.set(signature, HEAD);
  out.set(ciphertext, HEAD + SIGNATURE);
  return toBase64Url(out);
}

/** Opens a letter to the account `to` from `from`, checking `from` signed it. */
export async function openLetter(keys: AccountKeys, to: string, from: Correspondent, letter: string): Promise<unknown> {
  const fail = () => new VaultError('malformed', "This letter can't be opened: it's damaged, or not from who it says.");
  let bytes: Uint8Array<ArrayBuffer>;
  try {
    bytes = fromBase64Url(letter);
  } catch {
    throw fail();
  }
  if (bytes[0] !== VERSION || bytes.length < HEAD + SIGNATURE + 16) throw fail();
  const head = bytes.slice(0, HEAD);
  const signature = bytes.slice(HEAD, HEAD + SIGNATURE);
  const ciphertext = bytes.slice(HEAD + SIGNATURE);
  try {
    const verifier = await crypto.subtle.importKey('raw', fromBase64Url(from.signingPublic), SIGNING, false, ['verify']);
    if (!(await crypto.subtle.verify(SIGN, verifier, signature, signed(from.userId, to, head, ciphertext)))) throw fail();
    const key = await letterKey(keys.exchange.privateKey, from.exchangePublic, head.slice(1, 17), from.userId, to);
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: head.slice(17, 29), additionalData: context(from.userId, to) }, key, ciphertext);
    return JSON.parse(new TextDecoder().decode(plain)) as unknown;
  } catch {
    throw fail();
  }
}
